/**
 * The player-preferences slice (architecture.md §8): accessibility and display
 * preferences that belong to the player, not to any one game session.
 *
 * @remarks
 * Action names follow the past-tense "what happened" Redux convention
 * (`displayThemeSet`, not `setDisplayTheme`) — actions describe events, and the
 * reducer decides state.
 *
 * @packageDocumentation
 */

import type { MapStylePreference } from '@orpheus/player-host';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** Display themes the player app supports. `high_contrast` is a WCAG affordance, not a skin. */
export type DisplayTheme = 'dark' | 'high_contrast';

/**
 * D153: the player's word on the pack's clock. `'default'` defers to the
 * pack's `pacing.enabledByDefault` (Mike: the default is the game builder's,
 * in the data); `'on'` / `'off'` override it. A three-way rather than a
 * boolean so a player who never touched the control gets the pack's answer
 * — and a gentler second pack's — without the shell holding an opinion.
 */
export type AutoWaitPreference = 'default' | 'off' | 'on';

/** Transcript reveal pacing. `deliberate` is slower than `standard`; `instant` disables pacing. */
export type TextSpeed = 'deliberate' | 'instant' | 'standard';

/** The persisted player-preference state. */
export interface PlayerPreferencesState {
	/**
	 * Whether room plates are shown at all (R24 follow-up, Mike's ruling). A
	 * parser game must remain whole as pure text: D89 rule 1 keeps the picture
	 * off the input surface, and this keeps it optional. OFF gives the prose
	 * and the prompt the plate's vertical space back.
	 */
	/** D153: whether silence costs a turn — see {@link AutoWaitPreference}. */
	readonly autoWaitPreference: AutoWaitPreference;
	readonly displayTheme: DisplayTheme;
	/**
	 * R35 (D115): whether the ambience layer plays at all. Independent of the
	 * Images toggle by ruling (Calder's §8.1 — the eye and the ear are two
	 * axes): prose-and-atmosphere with no pictures is a coherent ask, and so
	 * is a silent film.
	 */
	readonly isAmbientAudioEnabled: boolean;
	/**
	 * D116/D117: the volume control's SETTING, 0..1 — the handle's position,
	 * not a gain. Renderers map it to gain perceptually (the web player cubes
	 * it), so storing the position keeps the round trip exact: what the
	 * player set is exactly what the slider shows forever after. Default 0.22
	 * (Mike's ear, once the readout was honest) ≈ 1% gain — the beds sit
	 * UNDER prose, so the slider is the loud option, never the rescue.
	 */
	readonly ambientAudioVolume: number;
	readonly isRoomImageryEnabled: boolean;
	/** Mirrors (and can override) the `prefers-reduced-motion` media query. */
	readonly isReducedMotionEnabled: boolean;
	/**
	 * D209: which of the pack's offered map styles the player looks at —
	 * `'default'` for the pack's opening style, or a style by name. Defined
	 * beside the function that meets it with a pack (`layOutMapView`), so the
	 * store holds the word and the host holds the rule; a chosen style the
	 * current pack does not offer reads as the default there, not here.
	 */
	readonly mapStylePreference: MapStylePreference;
	readonly textSpeed: TextSpeed;
}

/** The defaults a fresh profile starts from (dark/moody per the brand; motion respected). */
export const initialPlayerPreferencesState: PlayerPreferencesState = {
	// The ambience layer is opt-OUT for the same reason the imagery layer is —
	// and the browser's own gesture gate means nothing sounds until the player
	// acts anyway, so the default never ambushes an office.
	ambientAudioVolume: 0.22,
	autoWaitPreference: 'default',
	displayTheme: 'dark',
	isAmbientAudioEnabled: true,
	isReducedMotionEnabled: false,
	// The imagery layer is opt-OUT, not opt-in: a pack that ships plates meant
	// them to be seen, and a pack with none is unaffected either way.
	isRoomImageryEnabled: true,
	mapStylePreference: 'default',
	textSpeed: 'standard',
};

export const playerPreferencesSlice = createSlice({
	initialState: initialPlayerPreferencesState,
	name: 'playerPreferences',
	reducers: {
		ambientAudioPreferenceSet(state, action: PayloadAction<boolean>) {
			state.isAmbientAudioEnabled = action.payload;
		},
		ambientAudioVolumeSet(state, action: PayloadAction<number>) {
			// Clamped at the reducer so no consumer ever reads a wild value.
			state.ambientAudioVolume = Math.min(1, Math.max(0, action.payload));
		},
		autoWaitPreferenceSet(state, action: PayloadAction<AutoWaitPreference>) {
			state.autoWaitPreference = action.payload;
		},
		displayThemeSet(state, action: PayloadAction<DisplayTheme>) {
			state.displayTheme = action.payload;
		},
		mapStylePreferenceSet(state, action: PayloadAction<MapStylePreference>) {
			state.mapStylePreference = action.payload;
		},
		reducedMotionPreferenceSet(state, action: PayloadAction<boolean>) {
			state.isReducedMotionEnabled = action.payload;
		},
		roomImagesPreferenceSet(state, action: PayloadAction<boolean>) {
			state.isRoomImageryEnabled = action.payload;
		},
		textSpeedSet(state, action: PayloadAction<TextSpeed>) {
			state.textSpeed = action.payload;
		},
	},
	selectors: {
		selectAmbientAudioVolume: (state) => state.ambientAudioVolume,
		selectAutoWaitPreference: (state) => state.autoWaitPreference,
		selectDisplayTheme: (state) => state.displayTheme,
		selectIsAmbientAudioEnabled: (state) => state.isAmbientAudioEnabled,
		selectIsReducedMotionEnabled: (state) => state.isReducedMotionEnabled,
		selectIsRoomImageryEnabled: (state) => state.isRoomImageryEnabled,
		selectMapStylePreference: (state) => state.mapStylePreference,
		selectTextSpeed: (state) => state.textSpeed,
	},
});

export const {
	ambientAudioPreferenceSet,
	ambientAudioVolumeSet,
	autoWaitPreferenceSet,
	displayThemeSet,
	mapStylePreferenceSet,
	reducedMotionPreferenceSet,
	roomImagesPreferenceSet,
	textSpeedSet,
} = playerPreferencesSlice.actions;

export const {
	selectAmbientAudioVolume,
	selectAutoWaitPreference,
	selectDisplayTheme,
	selectIsAmbientAudioEnabled,
	selectIsReducedMotionEnabled,
	selectIsRoomImageryEnabled,
	selectMapStylePreference,
	selectTextSpeed,
} = playerPreferencesSlice.selectors;
